import { useTranslation } from "react-i18next";
import Header from "~/components/header/Header";
import Footer from "~/components/home/Footer";
import { HeroSection } from "~/components/products/pdp/HeroSection";
import { FleetBannerSection } from "~/components/FleetBannerSection";
import { HighlightsAccordion } from "~/components/products/HighlightsAccordion";
import { HighlightsCarousel } from "~/components/products/HighlightsCarousel";
import { PersonaSection } from "~/components/products/pdp/PersonaSection";
import { FeaturesSection } from "~/components/products/pdp/FeaturesSection";
import { ReviewsSection } from "~/components/products/pdp/ReviewsSection";
import { SpecsSection } from "~/components/products/pdp/SpecsSection";
import { ObdCompatibilityCheckerSection } from "~/components/products/pdp/ObdCompatibilityCheckerSection";
import { FaqSection } from "~/components/products/pdp/FaqSection";
import { CrossSellSection } from "~/components/products/pdp/CrossSellSection";
import { LastChanceSection } from "~/components/products/pdp/LastChanceSection";
import { useVariant } from "~/lib/experiments/context";
import type { ProductPageContent } from "~/data/products/types";

interface ProductPdpTemplateProps {
  content: ProductPageContent;
}

// No wrapping <main> here — root.tsx already provides the page's single <main> landmark.
// HighlightsAccordion's vehicle photo is still FPO in Figma — rendering a labeled
// placeholder there until a real asset lands.
export function ProductPdpTemplate({ content }: ProductPdpTemplateProps) {
  const { t } = useTranslation("products");
  // Decided server-side in the root loader and already present in the initial
  // HTML, so there is no post-hydration flip. Falls back to the control arm
  // ("accordion") on every route the experiment does not assign on — which is how
  // /products/view and /products/go stay on the accordion; `highlightsVariantB`
  // being undefined for them is the second belt.
  const highlightsVariant = useVariant("drive_highlights");

  return (
    <>
      <head>
        <title>{t(content.metaTitle)}</title>
        <meta name="description" content={t(content.metaDescription)} />
      </head>
      <Header variant="default" />
      <HeroSection name={content.name} productCode={content.productCode} content={content.hero} />
      <FleetBannerSection content={content.fleetBanner} />
      {highlightsVariant === "carousel" && content.highlightsVariantB ? (
        <HighlightsCarousel content={content.highlightsVariantB} />
      ) : (
        <HighlightsAccordion content={content.highlights} />
      )}
      <FeaturesSection content={content.features} />
      {content.persona && <PersonaSection content={content.persona} />}
      <ReviewsSection content={content.reviews} />
      <SpecsSection content={content.specs} />
      {content.obdCompatibilityChecker && <ObdCompatibilityCheckerSection />}
      <FaqSection content={content.faq} />
      <CrossSellSection content={content.crossSell} />
      <LastChanceSection productCode={content.productCode} content={content.lastChance} />
      <Footer />
    </>
  );
}
